import { useEffect, useState } from 'react';
import { fetchClaimStatus } from '../../api/externalSalesApi';
import { ClaimItem, ClaimStatus } from '../../api/types';
import { delay } from '../../delay';

export type ClaimStatusState =
  | { kind: 'finalizing' }
  | { kind: 'paid'; item: ClaimItem; orderNumber: string | null }
  | { kind: 'claimed' }
  | { kind: 'unpaid' }
  | { kind: 'unresolved'; operationId: string | null };

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

export function useClaimStatus(token: string, purchaseId?: string): ClaimStatusState {
  const [state, setState] = useState<ClaimStatusState>({ kind: 'finalizing' });
  const urlOperationId = purchaseId ?? (token || null);

  useEffect(() => {
    let active = true;

    if (!token) {
      setState({ kind: 'unresolved', operationId: urlOperationId });
      return () => {
        active = false;
      };
    }

    setState({ kind: 'finalizing' });

    const poll = async (attempt: number, lastOrderNumber: string | null): Promise<void> => {
      let claim: ClaimStatus;
      try {
        claim = await fetchClaimStatus(token);
      } catch (error) {
        // Neutral/unknown token or transport error: keep polling within the bound, then fall back.
        if (!active) {
          return;
        }
        if (attempt >= MAX_POLLS) {
          setState({ kind: 'unresolved', operationId: lastOrderNumber ?? urlOperationId });
          return;
        }
        await delay(POLL_INTERVAL_MS);
        if (active) {
          await poll(attempt + 1, lastOrderNumber);
        }
        return;
      }

      if (!active) {
        return;
      }
      if (claim.status === 'paid' && claim.item) {
        setState({ kind: 'paid', item: claim.item, orderNumber: claim.orderNumber });
        return;
      }
      // Redeemed in the app already (the buyer came back, or reloaded after the app-open link):
      // not an error on either path, so it never reaches the generic "something went wrong" state.
      if (claim.status === 'activated' || claim.status === 'granted') {
        setState({ kind: 'claimed' });
        return;
      }
      if (claim.status === 'failed' || claim.status === 'expired_unpaid') {
        setState({ kind: 'unpaid' });
        return;
      }
      if (attempt >= MAX_POLLS) {
        setState({ kind: 'unresolved', operationId: claim.orderNumber ?? urlOperationId });
        return;
      }
      await delay(POLL_INTERVAL_MS);
      if (active) {
        await poll(attempt + 1, claim.orderNumber);
      }
    };

    poll(1, null).catch(() => undefined);

    return () => {
      active = false;
    };
  }, [token, purchaseId, urlOperationId]);

  return state;
}
